import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth, api } from '../../context/AuthContext';
import { ArrowLeft, Save, Image as ImageIcon, X } from 'lucide-react';

const PROVINCES = ['SAN_JOSE', 'ALAJUELA', 'HEREDIA', 'CARTAGO', 'GUANACASTE', 'PUNTARENAS', 'LIMON'];
const CONDITIONS = ['NEW', 'USED'];
const DELIVERY_OPTIONS = [
  { id: 'pub', label: 'Recoger en persona (PUB)' },
  { id: 'dropoff', label: 'Entrega a domicilio' }
];
const DEFAULT_BRANDS = ['Honda', 'Yamaha', 'Kawasaki', 'Suzuki', 'KTM', 'TVS', 'Bajaj', 'AKT', 'Otro'];

export default function EditProduct() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [product, setProduct] = useState(null);
  const [newImages, setNewImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [removingImages, setRemovingImages] = useState([]);
  const [canFeature, setCanFeature] = useState(false);
  const [isFeatured, setIsFeatured] = useState(false);
  const [togglingFeatured, setTogglingFeatured] = useState(false);
  const [stock, setStock] = useState(1);
  const [lowStockThreshold, setLowStockThreshold] = useState(1);
  const [brands, setBrands] = useState(DEFAULT_BRANDS);
  const [categories, setCategories] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [isAccessory, setIsAccessory] = useState(false);
  const [accessorySubcategories, setAccessorySubcategories] = useState([]);
  
  const [form, setForm] = useState({
    title: '',
    description: '',
    price: '',
    negotiable: false,
    condition: 'NEW',
    province: 'SAN_JOSE',
    stock: 1,
    delivery_options: ['pub'],
    brand: '',
    category_id: '',
    is_accessory: false,
    accessory_subcategory: '',
    color: '',
    size: '',
    show_location: false
  });

  useEffect(() => {
    // Fetch brands
    const fetchBrands = async () => {
      try {
        const apiUrl = `/api`;
        const res = await fetch(`${apiUrl}/brands/visible`);
        const data = await res.json();
        if (data.brands && data.brands.length > 0) {
          setBrands([...data.brands, 'Otro']);
        }
      } catch (err) {
        console.error('Error fetching brands:', err);
      }
    };
    
    const fetchAccessorySubcategories = async () => {
      try {
        const apiUrl = `/api`;
        const res = await fetch(`${apiUrl}/accessories/subcategories`);
        const data = await res.json();
        setAccessorySubcategories(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching accessory subcategories:', err);
      }
    };
    
    fetchBrands();
    fetchAccessorySubcategories();
    
    const timer = setTimeout(() => {
      if (user === null) {
        navigate('/login');
        return;
      }
      setInitialLoading(false);
      
      if (!user || (user.role !== 'SELLER' && user.role !== 'ADMIN')) {
        navigate('/');
        return;
      }
      fetchProduct();
    }, 100);
    
    return () => clearTimeout(timer);
  }, [user]);

  // Fetch models when brand changes
  useEffect(() => {
    if (selectedBrand && selectedBrand !== 'ACCESORIOS') {
      const fetchModels = async () => {
        try {
          const apiUrl = `/api`;
          const res = await fetch(`${apiUrl}/categories/models/${selectedBrand}`);
          const data = await res.json();
          setCategories(Array.isArray(data) ? data : []);
        } catch (err) {
          console.error('Error fetching models:', err);
          setCategories([]);
        }
      };
      fetchModels();
    } else {
      setCategories([]);
    }
  }, [selectedBrand]);

  const fetchProduct = async () => {
    try {
      const res = await fetch(`/api/products/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!res.ok) {
        const errData = await res.json();
        console.error('Product fetch error:', errData);
        alert('Producto no encontrado o no tienes acceso');
        navigate('/seller/products');
        return;
      }
      
      const data = await res.json();
      if (data.status === 'DELETED') {
        alert('Este producto ha sido eliminado');
        navigate('/seller/products');
        return;
      }
      if (data.seller_id !== user?.id && user?.role !== 'ADMIN') {
        navigate('/seller/products');
        return;
      }
      
      setProduct(data);
      setExistingImages(data.images || []);
      setCanFeature(data.seller?.subscription_plan === 'PRO' || data.seller?.subscription_plan === 'PREMIUM');
      setIsFeatured(data.is_featured || false);
      setForm({
        title: data.title || '',
        description: data.description || '',
        price: data.price?.toString() || '',
        negotiable: data.negotiable || false,
        condition: data.condition || 'NEW',
        province: data.province || 'SAN_JOSE',
        stock: data.stock || 1,
        delivery_options: data.delivery_options || ['pub'],
        accessory_subcategory: data.accessory_subcategory || '',
        color: data.color || '',
        size: data.size || '',
        show_location: data.show_location || false
      });
      
      // Set brand/category from product
      // Check is_accessory flag directly instead of checking category
      const isAcc = data.is_accessory === true || data.is_accessory === 'true';
      setIsAccessory(isAcc);
      
      if (isAcc) {
        // It's an accessory - no brand/category needed
        setSelectedBrand('');
        setCategories([]);
        // Set accessory subcategory from product data
        if (data.accessory_subcategory) {
          setForm(prev => ({ ...prev, accessory_subcategory: data.accessory_subcategory }));
        }
      } else if (data.category && data.category.id) {
        // It's a part with a valid category
        const brandName = data.category.brand;
        setSelectedBrand(brandName);
        // Fetch models for this brand
        const fetchModels = async () => {
          try {
            const apiUrl = `/api`;
            const res = await fetch(`${apiUrl}/categories/models/${brandName}`);
            const modelData = await res.json();
            setCategories(Array.isArray(modelData) ? modelData : []);
            // Set the category ID if product has one
            if (data.category_id) {
              setForm(prev => ({ ...prev, category_id: data.category_id }));
            }
          } catch (err) {
            console.error('Error fetching models:', err);
          }
        };
        fetchModels();
      } else {
        // No category - treat as accessory
        setIsAccessory(true);
        setSelectedBrand('');
        setCategories([]);
      }
      
      // Load stock info
      loadStock(data.id);
    } catch (err) {
      console.error('Error fetching product:', err);
      alert('Producto no encontrado o error de conexión');
      navigate('/seller/products');
    }
  };

  const loadStock = async (productId) => {
    try {
      const res = await fetch(`/api/products/${productId}/stock`);
      if (res.ok) {
        const data = await res.json();
        setStock(data.quantity || 1);
        setLowStockThreshold(data.low_stock_threshold || 1);
      }
    } catch (err) {
      console.error('Error loading stock:', err);
    }
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const newImgs = files.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    setNewImages([...newImages, ...newImgs].slice(0, 10));
  };

  const removeNewImage = (index) => {
    const newImgs = [...newImages];
    URL.revokeObjectURL(newImgs[index].preview);
    newImgs.splice(index, 1);
    setNewImages(newImgs);
  };

  const removeExistingImage = async (imageId) => {
    setRemovingImages([...removingImages, imageId]);
    try {
      const res = await fetch(`/api/products/${id}/images/${imageId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Error eliminando imagen');
      }
      setExistingImages(prev => prev.filter(img => img.id !== imageId));
    } catch (err) {
      console.error('Error removing image:', err);
      alert(err.message);
    } finally {
      setRemovingImages(removingImages.filter(imgId => imgId !== imageId));
    }
  };

  const setPrimaryImage = async (imageId) => {
    try {
      await fetch(`/api/products/${id}/images/${imageId}/primary`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setExistingImages(prev => prev.map(img => ({ ...img, is_primary: img.id === imageId })));
    } catch (err) {
      console.error('Error setting primary:', err);
    }
  };

  const toggleFeatured = async () => {
    if (!canFeature) return;
    setTogglingFeatured(true);
    
    try {
      const token = localStorage.getItem('token');
      const endpoint = isFeatured ? 'DELETE' : 'POST';
      const url = `/api/products/${id}/feature`;
      
      const res = await fetch(url, {
        method: endpoint,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (res.ok) {
        setIsFeatured(!isFeatured);
      } else {
        const err = await res.json();
        alert(err.error || 'Error al cambiar destacado');
      }
    } catch (err) {
      alert('Error al cambiar destacado');
    } finally {
      setTogglingFeatured(false);
    }
  };

  const handleDeliveryChange = (option) => {
    const current = form.delivery_options || [];
    if (current.includes(option)) {
      setForm({ ...form, delivery_options: current.filter(o => o !== option) });
    } else {
      setForm({ ...form, delivery_options: [...current, option] });
    }
  };

  const handleBrandChange = (e) => {
    const value = e.target.value;
    if (value === 'ACCESORIOS') {
      setIsAccessory(true);
      setSelectedBrand('');
      setCategories([]);
      setForm({ ...form, is_accessory: true, brand: '', category_id: '', color: '', size: '' });
    } else {
      setIsAccessory(false);
      setSelectedBrand(value);
      setForm({ ...form, is_accessory: false, brand: value, category_id: '' });
      // Fetch models for this brand
      const fetchModels = async () => {
        try {
          const apiUrl = `/api`;
          const res = await fetch(`${apiUrl}/categories/models/${value}`);
          const data = await res.json();
          setCategories(Array.isArray(data) ? data : []);
        } catch (err) {
          console.error('Error fetching models:', err);
          setCategories([]);
        }
      };
      fetchModels();
    }
  };

  const handleCategoryChange = (e) => {
    setForm({ ...form, category_id: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Check at least 1 image (existing + new)
    if (existingImages.length === 0 && newImages.length < 1) {
      alert('El producto debe tener al menos 1 imagen');
      return;
    }
    setLoading(true);
    
    try {
      // Upload new images first if any
      if (newImages.length > 0) {
        const imgFormData = new FormData();
        newImages.forEach(img => imgFormData.append('images', img.file));
        
        const uploadRes = await fetch(`/api/products/${id}/images`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: imgFormData
        });
        
        if (!uploadRes.ok) {
          const err = await uploadRes.json();
          throw new Error(err.error || 'Error subiendo imágenes');
        }
        
        // Update existing images with newly uploaded ones
        const uploadData = await uploadRes.json();
        if (uploadData.images && uploadData.images.length > 0) {
          setExistingImages(prev => [...prev, ...uploadData.images]);
          setNewImages([]);
        }
      }
      
      // Then update product info
      const updateRes = await fetch(`/api/products/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          title: form.title,
          description: form.description,
          price: parseFloat(form.price),
          negotiable: form.negotiable,
          condition: form.condition,
          province: form.province,
          delivery_options: JSON.stringify(form.delivery_options || ['pub']),
          category_id: form.category_id || null,
          show_location: form.show_location,
          is_accessory: isAccessory,
          accessory_subcategory: form.accessory_subcategory || null,
          color: form.color || null,
          size: form.size || null,
          brand: isAccessory ? null : selectedBrand
        })
      });
      
      if (!updateRes.ok) {
        const errData = await updateRes.json();
        throw new Error(errData.error || 'Error actualizando producto');
      }
      
      // Update stock
      await fetch(`/api/products/${id}/stock`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ quantity: stock, low_stock_threshold: lowStockThreshold })
      });
      
      alert('Producto actualizado exitosamente');
      navigate('/seller/products');
    } catch (err) {
      console.error('Update error:', err);
      alert(err.message || 'Error al actualizar');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading || !product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const allImages = [...existingImages.map(img => ({ ...img, isExisting: true }))];

  return (
    <div className="min-h-screen">
      <div className="bg-secondary text-white py-8">
        <div className="max-w-3xl mx-auto px-4">
          <button 
            onClick={() => navigate('/seller/products')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver a Mis Productos
          </button>
          <h1 className="text-3xl font-bold">✏️ Editar Producto</h1>
          <p className="text-white/80 mt-2">Edita la información y fotos de tu producto</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Current Images */}
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h3 className="font-bold text-lg flex items-center gap-2 mb-4">
              <ImageIcon className="w-5 h-5" />
              Fotos actuales ({existingImages.length})
            </h3>
            
            <div className="grid grid-cols-5 gap-3 mb-4">
              {existingImages.map((img, i) => (
                <div key={img.id} className="relative aspect-square rounded-lg overflow-hidden border-2 border-primary">
                  <img src={img.url} alt="" className="w-full h-full object-cover" />
                  {img.is_primary ? (
                    <span className="absolute bottom-0 left-0 right-0 bg-primary text-white text-xs text-center py-1">
                      Principal
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setPrimaryImage(img.id)}
                      className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs text-center py-1 hover:bg-primary transition"
                    >
                      ★ Principal
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => removeExistingImage(img.id)}
                    disabled={removingImages.includes(img.id)}
                    className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 disabled:opacity-50"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            <h4 className="font-medium mb-2">Agregar nuevas fotos</h4>
            <div className="grid grid-cols-5 gap-3">
              {newImages.map((img, i) => (
                <div key={'new-' + i} className="relative aspect-square rounded-lg overflow-hidden border-2 border-dashed border-gray-300">
                  <img src={img.preview} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeNewImage(i)}
                    className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <label className="aspect-square border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary transition">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageChange}
                  className="hidden"
                />
                <ImageIcon className="w-8 h-8 text-gray-400" />
                <span className="text-xs text-gray-500 mt-1">Agregar</span>
              </label>
            </div>
            <p className="text-sm text-gray-500 mt-2">Máximo 10 fotos en total. Primera foto será la principal.</p>
          </div>

          {/* Product Info */}
          <div className="bg-white rounded-2xl p-6 shadow-sm space-y-4">
            <h3 className="font-bold text-lg">Información del producto</h3>
            
            <div>
              <label className="block font-medium mb-2">Título *</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary focus:border-primary"
                placeholder="Ej: Pastillas de freno Honda CB190R original"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block font-medium mb-2">Tipo de Artículo</label>
                <select
                  value={isAccessory ? 'ACCESORIOS' : selectedBrand}
                  onChange={handleBrandChange}
                  className="w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary"
                >
                  <option value="">Selecciona tipo</option>
                  <option value="ACCESORIOS">🔧 Accesorios</option>
                  {brands.map(b => <option key={b} value={b}>🔩 {b}</option>)}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {isAccessory ? '→ Tipo: Accesorio' : '→ Marca de moto'}
                </p>
              </div>
              {!isAccessory && selectedBrand && (
                <div>
                  <label className="block font-medium mb-2">Modelo de moto</label>
                  <select
                    value={form.category_id}
                    onChange={handleCategoryChange}
                    className="w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Selecciona modelo</option>
                    {categories.map(c => (
                      <option key={c.id} value={c.id}>{c.model} {c.year_start && `- ${c.year_start}`}</option>
                    ))}
                  </select>
                </div>
              )}
              {isAccessory && (
                <>
                  <div>
                    <label className="block font-medium mb-2">Tipo de accesorio</label>
                    <select
                      value={form.accessory_subcategory}
                      onChange={(e) => setForm({ ...form, accessory_subcategory: e.target.value })}
                      className="w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary"
                    >
                      <option value="">Selecciona tipo</option>
                      {accessorySubcategories.map(sub => (
                        <option key={sub.id} value={sub.id}>{sub.label}</option>
                      ))}
                    </select>
                    {accessorySubcategories.length === 0 && (
                      <p className="text-xs text-gray-500 mt-1">No hay subcategorías configuradas</p>
                    )}
                  </div>
                  <div>
                    <label className="block font-medium mb-2">Color</label>
                    <input
                      type="text"
                      value={form.color}
                      onChange={(e) => setForm({ ...form, color: e.target.value })}
                      className="w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary"
                      placeholder="Ej: Negro, Rojo"
                    />
                  </div>
                  <div>
                    <label className="block font-medium mb-2">Talla</label>
                    <input
                      type="text"
                      value={form.size}
                      onChange={(e) => setForm({ ...form, size: e.target.value })}
                      className="w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary"
                      placeholder="Ej: S, M, L, XL"
                    />
                  </div>
                </>
              )}
            </div>

            <div>
              <label className="block font-medium mb-2">Descripción</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary focus:border-primary"
                rows="4"
                placeholder="Describe tu producto..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block font-medium mb-2">Precio (₡) *</label>
                <input
                  type="number"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className="w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary"
                  placeholder="15000"
                  required
                />
              </div>
              
              <div>
                <label className="block font-medium mb-2">Existencias</label>
                <input
                  type="number"
                  min="0"
                  value={stock}
                  onChange={(e) => setStock(Math.max(0, parseInt(e.target.value) || 0))}
                  className="w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary"
                  placeholder="1"
                />
                <p className="text-xs text-gray-500 mt-1">0 = Agotado</p>
              </div>
              
              <div>
                <label className="block font-medium mb-2">Provincia</label>
                <select
                  value={form.province}
                  onChange={(e) => setForm({ ...form, province: e.target.value })}
                  className="w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary"
                >
                  {PROVINCES.map(p => (
                    <option key={p} value={p}>{p.replace('_', ' ')}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.negotiable}
                  onChange={(e) => setForm({ ...form, negotiable: e.target.checked })}
                  className="w-5 h-5"
                />
                <span>Precio negociable</span>
              </label>
            </div>

            <div>
              <label className="block font-medium mb-2">Condición</label>
              <div className="flex gap-4">
                {CONDITIONS.map(c => (
                  <label
                    key={c}
                    className={`flex-1 p-4 border-2 rounded-xl text-center cursor-pointer ${
                      form.condition === c ? 'border-primary bg-primary/5' : 'border-gray-200'
                    }`}
                  >
                    <input
                      type="radio"
                      name="condition"
                      value={c}
                      checked={form.condition === c}
                      onChange={() => setForm({ ...form, condition: c })}
                      className="hidden"
                    />
                    <span className="text-2xl">{c === 'NEW' ? '✨' : '🔄'}</span>
                    <p className="font-medium mt-2">{c === 'NEW' ? 'Nuevo' : 'Usado'}</p>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Delivery Options */}
          <div>
            <label className="block font-medium mb-2">Opciones de Entrega</label>
            <div className="grid grid-cols-2 gap-3">
              {DELIVERY_OPTIONS.map(opt => (
                <label
                  key={opt.id}
                  className={`p-4 border-2 rounded-xl cursor-pointer transition ${
                    (form.delivery_options || []).includes(opt.id)
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-200 hover:border-primary/50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={(form.delivery_options || []).includes(opt.id)}
                    onChange={() => handleDeliveryChange(opt.id)}
                    className="sr-only"
                  />
                  <span className="text-sm font-medium">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Featured toggle */}
          <div className={`rounded-xl p-4 border-2 ${
            canFeature ? 'bg-yellow-50 border-yellow-400' : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-3xl">{canFeature ? '⭐' : '🔒'}</span>
                <div>
                  <p className={`font-bold ${canFeature ? 'text-black' : 'text-gray-700'}`}>
                    {canFeature ? 'Producto Destacado' : 'Upgrade a PRO para destacar'}
                  </p>
                  <p className="text-sm text-gray-500">
                    {canFeature 
                      ? 'Los vendedores PRO y PREMIUM tienen productos destacados'
                      : 'Los vendedores PRO y PREMIUM tienen sus productos en la sección destacada'}
                  </p>
                </div>
              </div>
              
              {canFeature && (
                <button
                  onClick={toggleFeatured}
                  disabled={togglingFeatured}
                  className={`px-4 py-2 rounded-full font-medium transition ${
                    isFeatured 
                      ? 'bg-red-100 text-red-700 hover:bg-red-200 border-2 border-red-300'
                      : 'bg-green-100 text-green-700 hover:bg-green-200 border-2 border-green-300'
                  }`}
                >
                  {togglingFeatured ? '...' : isFeatured ? 'Quitar Destacado' : 'Marcar Destacado'}
                </button>
              )}
            </div>
          </div>

          {/* Submit */}
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.show_location}
                onChange={e => setForm({...form, show_location: e.target.checked})}
                className="w-5 h-5 accent-primary"
                disabled={!['PRO', 'PREMIUM'].includes(user?.subscription_plan)}
              />
              <div>
                <span className="font-bold text-black">📍 Mostrar ubicación de la tienda</span>
                <p className="text-sm text-gray-500">Los compradores verán el mapa con tu tienda en este producto</p>
                {!['PRO', 'PREMIUM'].includes(user?.subscription_plan) && (
                  <p className="text-xs text-orange-500 mt-0.5">Requiere plan PRO o PREMIUM</p>
                )}
              </div>
            </label>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-white py-4 rounded-full font-bold text-lg hover:bg-primary-dark transition flex items-center justify-center gap-2"
          >
            {loading ? (
              <div className="w-6 h-6 border-2 border-black border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Save className="w-5 h-5" />
                Guardar Cambios
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}